import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background noise-bg grid place-items-center p-6">
        <Card className="max-w-md w-full p-8 border border-border bg-card text-center" data-testid="reset-missing-token">
          <h1 className="font-heading text-2xl font-semibold">Missing reset token</h1>
          <p className="text-muted-foreground text-sm mt-2">The reset link seems incomplete. Please request a fresh email.</p>
          <Button onClick={() => nav("/login")} className="mt-6 bg-primary text-primary-foreground">Back to login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg grid place-items-center p-6">
      <Card className="max-w-md w-full p-8 border border-border bg-card" data-testid="reset-password-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl overflow-hidden grid place-items-center"><img src="/valuepool-logo.png" alt="Value Pool" className="w-full h-full object-contain" /></div>
          <div>
            <div className="font-heading text-lg font-semibold leading-tight">Value Pool</div>
            <div className="text-[10px] text-secondary">Pool of Smart Savings</div>
          </div>
        </div>

        {done ? (
          <div className="text-center space-y-3" data-testid="reset-success">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-heading text-2xl font-semibold">Password updated</h1>
            <p className="text-muted-foreground text-sm">You can now log in with your new password.</p>
            <Button onClick={() => nav("/login")} className="w-full bg-primary text-primary-foreground" data-testid="reset-go-login">Go to login</Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold">Choose a new password</h1>
            <p className="text-muted-foreground text-sm mt-1">Minimum 6 characters. Choose something you'll remember.</p>
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label>New password</Label>
                <Input data-testid="reset-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="••••••••" />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required placeholder="••••••••" />
              </div>
              <Button data-testid="reset-submit" disabled={busy} type="submit" className="w-full bg-primary text-primary-foreground">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-muted-foreground">
              <Link to="/login" className="text-primary font-medium hover:underline">Back to login</Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
